import type { AttemptClaimResponse, AttemptStartResponse, AttemptView, PublicOfferView } from "@townsy/contracts";
import { describeRewardError, type RewardFailure } from "./errors";

/**
 * The resident's sponsored-task attempt as a small, dependency-injected controller (docs/07 sections
 * 2-4, ADR-0011 #3-#6). It never decides an outcome: the server starts the attempt (reserving the reward
 * slot and the daily allowance), records the outbound click, checks the quiz answer, receives the
 * partner's signed callback and awards the coins. The browser only shows the server's attempt, runs
 * display countdowns from the server's timestamps, and asks again when one reaches zero.
 *
 *   intro -> starting -> website: open_link -> confirming_click -> counting -> claimable -> claimed
 *                        quiz:    answering --(wrong, tries left)--> answering --(no tries)--> failed
 *                                           --(right)--> claimed
 *                        partner: open_link -> waiting_partner -> (sponsor confirms) claimable | claimed
 *   any active phase -> expired (expiresAt passed) | canceled (resident gave up)
 *
 * The /out link (`outboundUrl`) comes only from a start or a refresh-link response and is rendered as a
 * plain anchor the resident clicks (no window.open after an await, ever).
 */

export type AttemptPhase =
  | "loading"
  | "intro"
  | "starting"
  | "open_link"
  | "confirming_click"
  | "counting"
  | "claimable"
  | "answering"
  | "waiting_partner"
  | "claimed"
  | "expired"
  | "canceled"
  | "failed"
  | "error";

export type AttemptBusy = "loading" | "starting" | "refreshing" | "claiming" | "canceling" | null;

export interface AttemptFlowState {
  /** The offer as listed (null when the dialog was opened for an attempt id only). */
  offer: PublicOfferView | null;
  attempt: AttemptView | null;
  /** The latest same-origin /out link from a start / refresh-link response. */
  outboundUrl: string | null;
  /** The resident clicked the link in this dialog (local only; the server records the real click). */
  linkOpened: boolean;
  busy: AttemptBusy;
  failure: RewardFailure | null;
  /** The server's claim receipt (coins awarded, balance after). */
  claim: AttemptClaimResponse["claim"] | null;
  claimReplayed: boolean;
  /** Website task: no click record arrived within the wait window (pop-up blocked, link opened elsewhere). */
  clickNotSeen: boolean;
}

export interface AttemptApi {
  start(offerId: string, idempotencyKey: string): Promise<AttemptStartResponse>;
  get(attemptId: string): Promise<AttemptView>;
  refreshLink(attemptId: string): Promise<AttemptView>;
  claim(attemptId: string, body: { answerIndex?: number }, idempotencyKey: string): Promise<AttemptClaimResponse>;
  cancel(attemptId: string): Promise<AttemptView>;
}

export interface AttemptDeps {
  api: AttemptApi;
  /** Server-adjusted epoch ms (lib/serverClock). */
  now(): number;
  sleep(ms: number): Promise<void>;
  makeKey(prefix: string): string;
  onChange(state: AttemptFlowState): void;
  /** A claim receipt from the server (the host toasts the new balance and refreshes caches). */
  onClaimed?(res: AttemptClaimResponse, info?: ClaimInfo): void;
  /** The server changed the attempt (started, canceled, ended): lists and balances are re-read. */
  onAttemptChanged?(attempt: AttemptView): void;
  /** How long to wait for the server's click record after the link was opened (default 30 s). */
  clickWaitMs?: number;
}

/** How a claim receipt came about (for the announcement; the receipt itself is the server's). */
export interface ClaimInfo {
  /**
   * True when this page watched the task while the server awarded it right after the sponsor's verified callback
   * (the receipt then says `replayed`, because the server's own claim came first, yet this is the resident's first
   * news of the reward). False for a task that was already claimed when it was opened here.
   */
  bySponsorWhileWatching: boolean;
}

export type AttemptInit = { offer: PublicOfferView; attemptId?: string | null } | { offer?: PublicOfferView | null; attemptId: string };

export const CLICK_WAIT_MS = 30_000;
/** Poll cadence while waiting for the click record (fast: the resident just clicked). */
export const CLICK_POLL_STEPS = [800, 1200, 1500, 2000, 2500, 3000] as const;
/** Poll cadence while waiting for the sponsor's confirmation (slower: it can take minutes). */
export const PARTNER_POLL_STEPS = [3000, 4000, 5000, 7000, 10_000] as const;

export const INITIAL_ATTEMPT_FLOW: AttemptFlowState = {
  offer: null,
  attempt: null,
  outboundUrl: null,
  linkOpened: false,
  busy: null,
  failure: null,
  claim: null,
  claimReplayed: false,
  clickNotSeen: false,
};

const TERMINAL: ReadonlySet<AttemptView["state"]> = new Set(["claimed", "expired", "canceled", "failed"]);

export function isTerminal(a: Pick<AttemptView, "state">): boolean {
  return TERMINAL.has(a.state);
}

/** Past the server's expiry by the (server-adjusted) clock. */
export function isPastExpiry(a: Pick<AttemptView, "expiresAt">, now: number): boolean {
  const t = Date.parse(a.expiresAt);
  return Number.isFinite(t) && now >= t;
}

/** Website task: a server click record exists and its minimum interval has passed. */
export function visitClaimable(a: Pick<AttemptView, "clickedAt" | "claimableAt">, now: number): boolean {
  if (!a.clickedAt) return false;
  const at = a.claimableAt ? Date.parse(a.claimableAt) : Number.NaN;
  return !Number.isFinite(at) || now >= at;
}

/** The phase shown for a state at a time (pure; the dialog renders from it). */
export function flowPhase(s: Pick<AttemptFlowState, "attempt" | "busy" | "failure" | "linkOpened" | "offer">, now: number): AttemptPhase {
  if (s.busy === "loading") return "loading";
  if (s.busy === "starting") return "starting";
  const a = s.attempt;
  if (!a) return s.offer ? "intro" : s.failure ? "error" : "loading";
  switch (a.state) {
    case "claimed":
      return "claimed";
    case "expired":
      return "expired";
    case "canceled":
      return "canceled";
    case "failed":
      return "failed";
    default:
      break;
  }
  if (isPastExpiry(a, now)) return "expired";
  if (a.kind === "quiz") return "answering";
  if (a.kind === "website_visit") {
    if (!a.clickedAt) return s.linkOpened ? "confirming_click" : "open_link";
    return visitClaimable(a, now) ? "claimable" : "counting";
  }
  if (a.partner?.confirmed) return "claimable";
  return s.linkOpened || a.clickedAt ? "waiting_partner" : "open_link";
}

/** A website / partner attempt that is still active needs a link the resident can click. */
export function needsLink(a: AttemptView): boolean {
  if (a.state !== "active" || a.kind === "quiz") return false;
  if (a.kind === "website_visit") return !a.clickedAt;
  return !a.partner?.confirmed && !a.clickedAt;
}

export class AttemptController {
  private s: AttemptFlowState;
  private readonly init: AttemptInit;
  private disposed = false;
  /** Bumped by every user action; results of an older action are dropped. */
  private op = 0;
  /** Bumped whenever polling must stop or restart. */
  private pollGen = 0;
  /** One Idempotency-Key per start intent, reused verbatim on a retry of the same start. */
  private startKey: string | null = null;
  /** The claim being submitted (same body + same key on a retry; a new answer gets a new key). */
  private pendingClaim: { attemptId: string; answer: number | null; key: string } | null = null;
  private offerId: string | null;

  constructor(
    private readonly deps: AttemptDeps,
    init: AttemptInit,
  ) {
    this.init = init;
    this.offerId = init.offer?.offerId ?? null;
    this.s = { ...INITIAL_ATTEMPT_FLOW, offer: init.offer ?? null };
  }

  get state(): AttemptFlowState {
    return this.s;
  }

  phase(now: number = this.deps.now()): AttemptPhase {
    return flowPhase(this.s, now);
  }

  /** Begin: resume the given (or the offer's own) active attempt, or wait for "Start task". */
  begin(): Promise<void> {
    const id = this.init.attemptId ?? this.init.offer?.activeAttemptId ?? null;
    if (id) return this.load(id);
    return Promise.resolve();
  }

  dispose(): void {
    this.disposed = true;
    this.pollGen++;
  }

  private set(patch: Partial<AttemptFlowState>): void {
    if (this.disposed) return;
    this.s = { ...this.s, ...patch };
    this.deps.onChange(this.s);
  }

  private next(): number {
    this.pollGen++;
    return ++this.op;
  }

  private live(token: number): boolean {
    return !this.disposed && token === this.op;
  }

  private seeAttempt(attempt: AttemptView, patch: Partial<AttemptFlowState> = {}): void {
    const before = this.s.attempt;
    if (attempt.offerId) this.offerId = attempt.offerId;
    this.set({ attempt, ...patch });
    if (!before || before.attemptId !== attempt.attemptId || before.state !== attempt.state) this.deps.onAttemptChanged?.(attempt);
  }

  // ------------------------------------------------------------------ load / resume

  /** Read an attempt (resume from a list, "show my task in progress"); mint a link when one is needed. */
  async load(attemptId: string): Promise<void> {
    const token = this.next();
    this.set({ busy: "loading", failure: null, claim: null, claimReplayed: false, clickNotSeen: false, outboundUrl: null, linkOpened: false });
    try {
      let attempt = await this.deps.api.get(attemptId);
      if (!this.live(token)) return;
      let outboundUrl: string | null = null;
      if (needsLink(attempt) && !isPastExpiry(attempt, this.deps.now())) {
        try {
          attempt = await this.deps.api.refreshLink(attemptId);
          outboundUrl = attempt.outboundUrl;
        } catch (err) {
          if (!this.live(token)) return;
          // The attempt itself loaded: show it with the link problem ("Get a fresh link" retries).
          this.set({ failure: describeRewardError(err, { stage: "refresh", kind: attempt.kind }) });
        }
        if (!this.live(token)) return;
      }
      this.seeAttempt(attempt, { busy: null, outboundUrl });
      this.afterSeen(token);
    } catch (err) {
      if (!this.live(token)) return;
      this.set({ busy: null, failure: describeRewardError(err, { stage: "load" }) });
    }
  }

  /** Show another attempt (ATTEMPT_IN_PROGRESS -> "Show my task in progress"). */
  switchTo(attemptId: string): Promise<void> {
    if (this.s.attempt?.attemptId !== attemptId) this.set({ offer: this.s.offer && this.s.offer.activeAttemptId === attemptId ? this.s.offer : null, attempt: null });
    return this.load(attemptId);
  }

  /** Continue whatever the server state needs (claim receipt after an automatic partner claim, polling). */
  private afterSeen(token: number): void {
    const a = this.s.attempt;
    if (!a || !this.live(token)) return;
    if (a.state === "claimed" && !this.s.claim) {
      void this.fetchReceipt(token);
      return;
    }
    if (a.state !== "active" || isPastExpiry(a, this.deps.now())) return;
    if (a.kind === "partner_callback" && !a.partner?.confirmed && (a.clickedAt || this.s.linkOpened)) void this.pollPartner(token);
  }

  // ------------------------------------------------------------------ start

  /** "Start task": the server checks eligibility and reserves the reward slot + daily allowance. */
  async start(): Promise<void> {
    const offerId = this.offerId ?? this.s.offer?.offerId ?? null;
    if (!offerId || this.s.busy) return;
    if (this.s.attempt && this.s.attempt.state === "active" && !isPastExpiry(this.s.attempt, this.deps.now())) return;
    if (!this.startKey) this.startKey = this.deps.makeKey("attempt");
    const key = this.startKey;
    const token = this.next();
    this.set({ busy: "starting", failure: null, claim: null, claimReplayed: false, clickNotSeen: false, linkOpened: false, outboundUrl: null, attempt: null });
    try {
      const res = await this.deps.api.start(offerId, key);
      if (!this.live(token)) return;
      this.startKey = null;
      this.seeAttempt(res.attempt, { busy: null, outboundUrl: res.attempt.outboundUrl });
      this.afterSeen(token);
    } catch (err) {
      if (!this.live(token)) return;
      const failure = describeRewardError(err, { stage: "start", kind: this.s.offer?.kind ?? null });
      // A retryable failure resends the SAME key (the server answers a repeated key with the same attempt).
      if (failure.action !== "retry") this.startKey = null;
      this.set({ busy: null, failure });
    }
  }

  /** After an attempt ended (expired / canceled): a NEW start (new key) if the task is still available. */
  restart(): Promise<void> {
    this.startKey = null;
    this.pendingClaim = null;
    this.set({ attempt: null, claim: null, failure: null, outboundUrl: null, linkOpened: false, clickNotSeen: false });
    return this.start();
  }

  // ------------------------------------------------------------------ link

  /** The resident clicked the /out anchor (synchronously, from the click handler). */
  linkOpened(): void {
    const a = this.s.attempt;
    if (!a || a.state !== "active") return;
    const token = this.next();
    this.set({ linkOpened: true, clickNotSeen: false, failure: null });
    if (a.kind === "website_visit" && !a.clickedAt) void this.pollForClick(token);
    else if (a.kind === "partner_callback" && !a.partner?.confirmed) void this.pollPartner(token);
  }

  /** A fresh /out link (the previous one was used, expired with the page, or never arrived). */
  async refreshLink(): Promise<void> {
    const a = this.s.attempt;
    if (!a || a.state !== "active" || a.kind === "quiz" || this.s.busy) return;
    const token = this.next();
    this.set({ busy: "refreshing", failure: null });
    try {
      const fresh = await this.deps.api.refreshLink(a.attemptId);
      if (!this.live(token)) return;
      const firstClickPending = fresh.kind === "website_visit" ? !fresh.clickedAt : !fresh.partner?.confirmed && !fresh.clickedAt;
      this.seeAttempt(fresh, { busy: null, outboundUrl: fresh.outboundUrl, linkOpened: firstClickPending ? false : this.s.linkOpened, clickNotSeen: false });
      this.afterSeen(token);
    } catch (err) {
      if (!this.live(token)) return;
      const failure = describeRewardError(err, { stage: "refresh", kind: a.kind });
      this.set({ busy: null, failure });
      if (failure.action === "reload_attempt") await this.reload(token);
    }
  }

  // ------------------------------------------------------------------ claim

  /** "Claim coins" (website / partner) or "Check answer" (quiz, with the chosen index). */
  async claim(answerIndex?: number): Promise<void> {
    const a = this.s.attempt;
    if (!a || a.state !== "active" || this.s.busy) return;
    if (a.kind === "quiz" && (answerIndex === undefined || !Number.isInteger(answerIndex))) {
      this.set({ failure: { code: "VALIDATION_FAILED", message: "Choose an answer first.", action: "answer_again" } });
      return;
    }
    const answer = a.kind === "quiz" ? (answerIndex as number) : null;
    if (!this.pendingClaim || this.pendingClaim.attemptId !== a.attemptId || this.pendingClaim.answer !== answer) {
      this.pendingClaim = { attemptId: a.attemptId, answer, key: this.deps.makeKey("claim") };
    }
    const { key } = this.pendingClaim;
    const token = this.next();
    this.set({ busy: "claiming", failure: null });
    try {
      const res = await this.deps.api.claim(a.attemptId, answer === null ? {} : { answerIndex: answer }, key);
      if (!this.live(token)) return;
      this.pendingClaim = null;
      this.seeAttempt(res.attempt, { busy: null, claim: res.claim, claimReplayed: res.replayed });
      this.deps.onClaimed?.(res);
    } catch (err) {
      if (!this.live(token)) return;
      const failure = describeRewardError(err, { stage: "claim", kind: a.kind });
      if (failure.action !== "retry") this.pendingClaim = null;
      if (failure.action === "answer_again" && a.quiz && failure.triesLeft !== null && failure.triesLeft !== undefined) {
        // The server counted the wrong answer: show its count of tries left.
        this.set({ busy: null, failure, attempt: { ...a, quiz: { ...a.quiz, triesLeft: failure.triesLeft } } });
        return;
      }
      if (failure.code === "CLAIM_TOO_EARLY" && failure.claimableAt && a.kind === "website_visit") {
        this.set({ busy: null, failure, attempt: { ...a, claimableAt: failure.claimableAt } });
        await this.reload(token);
        return;
      }
      this.set({ busy: null, failure });
      if (failure.action === "reload_attempt") await this.reload(token);
    }
  }

  /** The attempt was claimed by the server (partner callback): read the receipt (a repeated claim awards nothing). */
  private async fetchReceipt(token: number, watched = false): Promise<void> {
    const a = this.s.attempt;
    if (!a) return;
    try {
      const res = await this.deps.api.claim(a.attemptId, {}, this.deps.makeKey("claim"));
      if (!this.live(token)) return;
      this.seeAttempt(res.attempt, { claim: res.claim, claimReplayed: res.replayed });
      this.deps.onClaimed?.(res, { bySponsorWhileWatching: watched });
    } catch {
      /* The attempt shows as claimed either way; the wallet chip re-reads the balance. */
    }
  }

  // ------------------------------------------------------------------ cancel / expiry

  /** Give up the attempt: the server releases the reserved budget and allowance once. */
  async cancel(): Promise<void> {
    const a = this.s.attempt;
    if (!a || a.state !== "active" || this.s.busy) return;
    const token = this.next();
    this.set({ busy: "canceling", failure: null });
    try {
      const after = await this.deps.api.cancel(a.attemptId);
      if (!this.live(token)) return;
      this.seeAttempt(after, { busy: null, outboundUrl: null });
      this.afterSeen(token);
    } catch (err) {
      if (!this.live(token)) return;
      const failure = describeRewardError(err, { stage: "cancel", kind: a.kind });
      this.set({ busy: null, failure });
      if (failure.action === "reload_attempt" || failure.code === "NOT_FOUND") await this.reload(token);
    }
  }

  /** The expiry countdown reached zero: ask the server (the attempt may have been claimed at the last second). */
  async expiryReached(): Promise<void> {
    const a = this.s.attempt;
    if (!a || a.state !== "active" || this.s.busy) return;
    const token = this.next();
    await this.reload(token);
    const after = this.s.attempt;
    if (after && after.state === "active" && isPastExpiry(after, this.deps.now())) this.deps.onAttemptChanged?.({ ...after, state: "expired" });
  }

  /** Re-read the attempt and continue from it (keeps the shown failure). */
  async reload(token: number = this.next()): Promise<void> {
    const a = this.s.attempt;
    if (!a) return;
    try {
      const fresh = await this.deps.api.get(a.attemptId);
      if (!this.live(token)) return;
      this.seeAttempt(fresh);
      this.afterSeen(token);
    } catch {
      /* Keep showing what we have; the next action or the countdown asks again. */
    }
  }

  // ------------------------------------------------------------------ polling

  /** Website task: wait for the server's click record (the /out redirect writes it). */
  private async pollForClick(token: number): Promise<void> {
    const a0 = this.s.attempt;
    if (!a0) return;
    const gen = ++this.pollGen;
    const startedAt = this.deps.now();
    const limit = this.deps.clickWaitMs ?? CLICK_WAIT_MS;
    let i = 0;
    for (;;) {
      await this.deps.sleep(CLICK_POLL_STEPS[Math.min(i++, CLICK_POLL_STEPS.length - 1)]!);
      if (!this.live(token) || gen !== this.pollGen) return;
      try {
        const fresh = await this.deps.api.get(a0.attemptId);
        if (!this.live(token) || gen !== this.pollGen) return;
        this.seeAttempt(fresh);
        if (fresh.clickedAt || fresh.state !== "active") return;
      } catch {
        /* transient: keep waiting until the window ends */
      }
      const now = this.deps.now();
      if (isPastExpiry(a0, now)) return;
      if (now - startedAt >= limit) {
        this.set({ clickNotSeen: true });
        return;
      }
    }
  }

  /** Partner task: wait for the sponsor's signed confirmation (Townsy usually claims it right away). */
  private async pollPartner(token: number): Promise<void> {
    const a0 = this.s.attempt;
    if (!a0) return;
    const gen = ++this.pollGen;
    let i = 0;
    let failures = 0;
    for (;;) {
      const step = PARTNER_POLL_STEPS[Math.min(i++, PARTNER_POLL_STEPS.length - 1)]!;
      const untilExpiry = Date.parse(a0.expiresAt) - this.deps.now();
      // One last look right after the expiry, never past it.
      await this.deps.sleep(Math.max(250, Math.min(failures > 0 ? Math.min(30_000, step * (failures + 1)) : step, untilExpiry + 250)));
      if (!this.live(token) || gen !== this.pollGen) return;
      try {
        const fresh = await this.deps.api.get(a0.attemptId);
        if (!this.live(token) || gen !== this.pollGen) return;
        failures = 0;
        this.seeAttempt(fresh);
        if (fresh.state === "claimed") {
          // This page watched the task go from waiting to claimed: the sponsor's callback just paid it.
          if (!this.s.claim) await this.fetchReceipt(token, true);
          return;
        }
        if (fresh.state !== "active" || fresh.partner?.confirmed) return;
        if (isPastExpiry(fresh, this.deps.now())) return;
      } catch {
        failures++;
        if (isPastExpiry(a0, this.deps.now())) return;
      }
    }
  }
}
